import {DataTable} from "../DataTable/DataTable";
import {useTranslation} from "react-i18next";
import {Box, Modal, ModalProps} from "@mantine/core";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {MouseEvent, useCallback, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {EntityActionGroup} from "../../group/EntityActionGroup/EntityActionGroup";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {ServiceTypeFacetEntity} from "@ots-frontend/ots-data";
import {useServiceTypeFacetDataTableFilter} from "./useServiceTypeFacetDataTableFilter";
import {useServiceTypeFacetMutation} from "../../../hooks/useServiceTypeFacetMutation";

const EMPTY_ARRAY: ServiceTypeFacetEntity[] = []

export type ServiceEventTypeDataTableProps = EntityDataTableProps<ServiceTypeFacetEntity>

export const ServiceTypeFacetDataTable = (props: ServiceEventTypeDataTableProps) => {
  const {
    records: facets = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useServiceTypeFacetDataTableFilter({
    records: facets
  })

  const sortConfig = useMemo(() => {
    return {
      "facet": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.facet?.toLowerCase()
          }])
        }
      },
      "resetStatus": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.resetStatus?.toLowerCase()
          }])
        }
      },
      "defaultBaselineStatus": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.defaultBaselineStatus?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'facet', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const {
    deleteServiceTypeFacet,
  } = useServiceTypeFacetMutation()

  const onDeleteActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: ServiceTypeFacetEntity) => {
    e.stopPropagation()
    deleteServiceTypeFacet(record.id, undefined)
  }, [deleteServiceTypeFacet])

  const columns = useMemo(() => {
    const values: DataTableColumn<ServiceTypeFacetEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "facet"),
        title: t("common.entity.servicetypefacet.table.column.facet", "Facet"),
        accessor: 'facet',
        width: 150,
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "resetStatus"),
        title: t("common.entity.servicetypefacet.table.column.resetstatus", "Reset Status"),
        accessor: 'resetStatus',
        width: 150,
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "defaultBaselineStatus"),
        title: t("common.entity.servicetypefacet.table.column.defaultbaselinestatus", "Default Baseline Status"),
        accessor: 'defaultBaselineStatus',
        width: 150,
        sortable: true,
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: (company) => (
          <EntityActionGroup
            permissionPrefix={"servicetype"}
            view={false}
            edit={false}
            delete={true}
            onDeleteActionClick={(e) => onDeleteActionClick(e, company)}
          />
        )
      }
    ]

    return values
  }, [filterConfig, onDeleteActionClick, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}
